// Reusable dialog for creating or editing a spam rule.
// Used by SpamRulesView (full CRUD) and the Spam button in the agent/triage
// views (new-rule only, pre-filled with conversation context).

import { useEffect, useRef, useState } from 'react'
import {
  Autocomplete, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputLabel, ListItemText, MenuItem, Select, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material'
import { adminSpam } from './api.js'
import { intentOptionsFor } from './constants/intents.js'
import { KNOWN_CHANNELS, CHANNEL_LABELS } from './constants/channels.js'
import { KNOWN_CLIENTS } from './constants/clients.js'
import { KNOWN_COMPANIES } from './constants/companies.js'
import { TEAMS, teamLabel } from '@cxhub/shared'
import ExpandableTextField from './ExpandableTextField.jsx'

// channel/client/company are ALWAYS arrays in form state, whether creating
// or editing — both modes render the same multi-select now (see
// MultiSelectField below). Editing keeps the rule's original value
// locked/always-checked (never deselectable, so an edit can only ADD scope
// via fan-out, never reassign or drop the row's own identity) — Company,
// Channel, and Client are required, at least one entry each.
const EMPTY_FORM = {
  name: '',
  description: '',
  isActive: true,
  channel: [],
  client: [],
  company: [],
  fromPatterns: [],
  toPattern: '',
  subjectPatterns: [],
  bodyPattern: '',
  bodyPatternType: 'substring',
  intent: '',
  category: '',
  // Team (2026-10-07): a rule fires only on a conversation whose effective
  // team matches. Default Customer Service, what every existing rule is.
  team: 'customer_service',
}

// Both create (POST) and edit (PUT) now send the channels/clients/companies
// array shape unconditionally -- the server fans out into one row per
// combination either way, collapsing to a plain single-row update/insert
// when the cross-product is exactly one combo (see admin-spam.js's PUT route
// and updateSpamRuleWithFanOut()).
function toApi(form) {
  return {
    name: form.name.trim(),
    description: form.description.trim() || null,
    isActive: form.isActive,
    fromPatterns: form.fromPatterns.length ? form.fromPatterns : null,
    toPattern: form.toPattern.trim() || null,
    subjectPatterns: form.subjectPatterns.length ? form.subjectPatterns : null,
    bodyPattern: form.bodyPattern.trim() || null,
    bodyPatternType: form.bodyPatternType || 'substring',
    intent: form.intent || null,
    category: form.category.trim() || null,
    team: form.team || 'customer_service',
    channels: form.channel,
    clients: form.client,
    companies: form.company,
  }
}

function fromRule(rule) {
  return {
    name: rule.name ?? '',
    description: rule.description ?? '',
    isActive: rule.isActive ?? true,
    channel: rule.channel ? [rule.channel] : [],
    client: rule.client ? [rule.client] : [],
    company: rule.company ? [rule.company] : [],
    fromPatterns: rule.fromPatterns ?? [],
    toPattern: rule.toPattern ?? '',
    subjectPatterns: rule.subjectPatterns ?? [],
    bodyPattern: rule.bodyPattern ?? '',
    bodyPatternType: rule.bodyPatternType ?? 'substring',
    intent: rule.intent ?? '',
    category: rule.category ?? '',
    team: rule.team ?? 'customer_service',
  }
}

// Shared multi-select for Channel/Client/Company -- used identically whether
// creating (nothing locked) or editing (lockedValue is the rule's ORIGINAL
// value: its checkbox stays disabled/always-checked, so an edit can only ADD
// scope via fan-out, never reassign or drop the row off its own original
// value). onChange defensively re-adds lockedValue if it's ever missing from
// the emitted selection -- belt and suspenders alongside the disabled
// checkbox, which already blocks removing it through the UI.
function MultiSelectField({ label, options, labelFor = (o) => o, value, onChange, lockedValue }) {
  function handleChange(e) {
    let next = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
    if (lockedValue && !next.includes(lockedValue)) next = [...next, lockedValue]
    onChange(next)
  }
  return (
    <FormControl size="small" fullWidth required>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        label={label}
        onChange={handleChange}
        renderValue={(selected) => (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {selected.map((v) => <Chip key={v} label={labelFor(v)} size="small" />)}
          </Stack>
        )}
      >
        {options.map((o) => {
          const isLocked = o === lockedValue
          const item = (
            <MenuItem key={o} value={o} disabled={isLocked}>
              <Checkbox checked={value.includes(o)} size="small" disabled={isLocked} />
              <ListItemText primary={labelFor(o)} />
            </MenuItem>
          )
          return isLocked
            ? <Tooltip key={o} title="Can't remove — this rule's original value" placement="right"><span>{item}</span></Tooltip>
            : item
        })}
      </Select>
    </FormControl>
  )
}

// initialValues: partial form values to pre-fill when creating a new rule
// (e.g. { channel: ['ebay'], client: ['eBay'], company: ['1A'], fromPatterns: ['buyer123'], name: 'Spam from buyer123' }).
// categoryOptions: distinct category labels already in use elsewhere, offered
// as Autocomplete suggestions so similar rules converge on the same label
// (e.g. "Auto Reply") instead of drifting into near-duplicates.
export default function SpamRuleDialog({ open, rule, initialValues, onClose, onSaved, categoryOptions = [] }) {
  const [form, setForm] = useState(EMPTY_FORM)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [minimized, setMinimized] = useState(false)
  // Filters (dims, never removes) the Subject-contains chip list below --
  // only shown once there's more than one phrase to search through. See
  // that field's own comment for why dimming rather than filtering the
  // Autocomplete's own value is the safe choice here.
  const [subjectFilter, setSubjectFilter] = useState('')
  // Same filter-dims-never-removes treatment for the From-contains chip list.
  const [fromFilter, setFromFilter] = useState('')
  // Tracks what's currently typed into the Subject-contains field (not yet
  // committed as a chip) -- lets the helper text below warn "already in the
  // list" the moment the typed text matches an existing phrase, rather than
  // the user pressing enter and nothing visibly happening. MUI's Autocomplete
  // silently refuses to add an exact duplicate freeSolo value on its own
  // (no new chip, no error) -- that refusal is correct/desired (a duplicate
  // phrase adds nothing to containsAny()'s matching), it just gave no
  // feedback before this.
  const [subjectInputValue, setSubjectInputValue] = useState('')
  // Same duplicate-typed-value warning treatment for From.
  const [fromInputValue, setFromInputValue] = useState('')
  const prevOpenRef = useRef(false)

  // Seed form state only when the dialog transitions from closed to open.
  // Using a ref to detect the false→true transition avoids re-seeding when
  // initialValues gets a new object reference on parent re-renders (e.g. from
  // the presence heartbeat) while the user is actively filling in the form.
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setForm(rule ? fromRule(rule) : { ...EMPTY_FORM, ...(initialValues ?? {}) })
      setError(null)
      setMinimized(false)
      setSubjectFilter('')
      setFromFilter('')
      setSubjectInputValue('')
      setFromInputValue('')
    }
    prevOpenRef.current = open
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function set(key, val) {
    setForm((f) => ({ ...f, [key]: val }))
  }

  async function handleSave() {
    if (!form.name.trim()) { setError('Name is required.'); return }
    if (!form.company.length) { setError('At least one company is required.'); return }
    if (!form.channel.length) { setError('At least one channel is required.'); return }
    if (!form.client.length) { setError('At least one client is required.'); return }
    if (form.fromPatterns.length > 1 && form.subjectPatterns.length > 1) {
      setError("A rule can't have multiple From values and multiple Subject phrases at the same time — narrow one of them to a single value.")
      return
    }
    setBusy(true); setError(null)
    try {
      const data = toApi(form)
      const saved = rule ? await adminSpam.update(rule.id, data) : await adminSpam.create(data)
      onSaved(saved, !!rule)
    } catch (e) {
      setError(e.message || 'Save failed.')
    } finally {
      setBusy(false)
    }
  }

  // Display-only -- storage/matching (spam.js's containsAny()) are both
  // order-independent, so re-deriving this every render and letting
  // onChange feed the (re-sorted, edited) result straight back into
  // form.subjectPatterns is safe; there's no "real" order to preserve.
  const sortedSubjectPatterns = [...form.subjectPatterns].sort((a, b) => a.localeCompare(b))
  const sortedFromPatterns = [...form.fromPatterns].sort((a, b) => a.localeCompare(b))
  const subjectInputIsDuplicate = subjectInputValue.trim() !== '' &&
    form.subjectPatterns.some((p) => p.toLowerCase() === subjectInputValue.trim().toLowerCase())
  const fromInputIsDuplicate = fromInputValue.trim() !== '' &&
    form.fromPatterns.some((p) => p.toLowerCase() === fromInputValue.trim().toLowerCase())

  return (
    <Dialog
      open={open}
      onClose={minimized ? undefined : onClose}
      maxWidth={minimized ? false : 'md'}
      fullWidth={!minimized}
      slotProps={minimized ? {
        backdrop: { sx: { opacity: 0, pointerEvents: 'none' } },
      } : undefined}
      sx={minimized ? {
        pointerEvents: 'none',
        '& .MuiDialog-container': {
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          pr: 2,
          pb: 2,
        },
      } : undefined}
      PaperProps={{
        elevation: 8,
        sx: minimized ? { pointerEvents: 'auto', m: 0, width: 300 } : undefined,
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          cursor: minimized ? 'pointer' : 'default',
          userSelect: 'none',
        }}
        onClick={minimized ? () => setMinimized(false) : undefined}
      >
        <span>{rule ? 'Edit Spam Rule' : 'New Spam Rule'}</span>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setMinimized((m) => !m) }}
          title={minimized ? 'Restore' : 'Minimize'}
          sx={{ ml: 1 }}
        >
          {minimized ? '▲' : '—'}
        </IconButton>
      </DialogTitle>

      {!minimized && (
        <>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              A message is marked spam when ALL specified conditions match. Leave a field blank to skip that condition.
            </Typography>

            <TextField
              label="Rule Name" required size="small" fullWidth
              value={form.name} onChange={(e) => set('name', e.target.value)}
            />

            <Stack direction="row" alignItems="center" spacing={1}>
              <Switch checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} size="small" />
              <Typography variant="body2">{form.isActive ? 'Active' : 'Inactive'}</Typography>
            </Stack>

            <MultiSelectField
              label="Channel"
              options={KNOWN_CHANNELS}
              labelFor={(c) => CHANNEL_LABELS[c] ?? c}
              value={form.channel}
              onChange={(val) => set('channel', val)}
              lockedValue={rule?.channel}
            />

            <MultiSelectField
              label="Client"
              options={KNOWN_CLIENTS}
              value={form.client}
              onChange={(val) => set('client', val)}
              lockedValue={rule?.client}
            />

            <MultiSelectField
              label="Company"
              options={KNOWN_COMPANIES}
              value={form.company}
              onChange={(val) => set('company', val)}
              lockedValue={rule?.company}
            />

            <FormControl size="small" fullWidth>
              <InputLabel>Team</InputLabel>
              <Select value={form.team} label="Team" onChange={(e) => set('team', e.target.value)} SelectDisplayProps={{ 'data-testid': 'spam-rule-team' }}>
                {TEAMS.map((t) => <MenuItem key={t} value={t}>{teamLabel(t)}</MenuItem>)}
              </Select>
            </FormControl>

            {form.fromPatterns.length > 1 && (
              <TextField
                label="Filter values" size="small" fullWidth
                value={fromFilter} onChange={(e) => setFromFilter(e.target.value)}
                placeholder="Type to highlight a value below…"
              />
            )}

            <Autocomplete
              multiple freeSolo size="small" fullWidth
              options={[]}
              value={sortedFromPatterns}
              onChange={(_, val) => set('fromPatterns', val)}
              // Tracks the not-yet-committed typed text so the helper text
              // below can warn on an exact-duplicate BEFORE enter is
              // pressed -- MUI already silently refuses to add a duplicate
              // freeSolo chip (no new chip, no event), which previously
              // gave no feedback at all.
              inputValue={fromInputValue}
              onInputChange={(_, val) => setFromInputValue(val)}
              renderTags={(value, getTagProps) =>
                value.map((phrase, index) => {
                  // Dims (never removes) a chip that doesn't match the
                  // filter box above -- the full array is always what's
                  // passed to onChange, so this can never silently drop a
                  // value that's just scrolled out of view/dimmed.
                  const dimmed = fromFilter.trim() && !phrase.toLowerCase().includes(fromFilter.trim().toLowerCase())
                  return (
                    <Chip
                      label={phrase} size="small" {...getTagProps({ index })} key={phrase + index}
                      sx={dimmed ? { opacity: 0.35 } : undefined}
                    />
                  )
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="From contains"
                  helperText={fromInputIsDuplicate
                    ? 'Already in the list below — pressing enter won\'t add a second copy.'
                    : 'Matches if the sender contains ANY of these values — press enter after each'}
                  FormHelperTextProps={fromInputIsDuplicate ? { sx: { color: 'warning.main', fontWeight: 600 } } : undefined}
                />
              )}
            />

            <TextField
              label="To address contains" size="small" fullWidth
              value={form.toPattern} onChange={(e) => set('toPattern', e.target.value)}
              helperText="Recipient address — for email channels (not used for eBay)"
            />

            {form.subjectPatterns.length > 1 && (
              <TextField
                label="Filter phrases" size="small" fullWidth
                value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)}
                placeholder="Type to highlight a phrase below…"
              />
            )}

            <Autocomplete
              multiple freeSolo size="small" fullWidth
              options={[]}
              value={sortedSubjectPatterns}
              onChange={(_, val) => set('subjectPatterns', val)}
              // Tracks the not-yet-committed typed text so the helper text
              // below can warn on an exact-duplicate BEFORE enter is
              // pressed -- MUI already silently refuses to add a duplicate
              // freeSolo chip (no new chip, no event), which previously
              // gave no feedback at all.
              inputValue={subjectInputValue}
              onInputChange={(_, val) => setSubjectInputValue(val)}
              renderTags={(value, getTagProps) =>
                value.map((phrase, index) => {
                  // Dims (never removes) a chip that doesn't match the
                  // filter box above -- the full array is always what's
                  // passed to onChange, so this can never silently drop a
                  // phrase that's just scrolled out of view/dimmed.
                  const dimmed = subjectFilter.trim() && !phrase.toLowerCase().includes(subjectFilter.trim().toLowerCase())
                  return (
                    <Chip
                      label={phrase} size="small" {...getTagProps({ index })} key={phrase + index}
                      sx={dimmed ? { opacity: 0.35 } : undefined}
                    />
                  )
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Subject contains"
                  helperText={subjectInputIsDuplicate
                    ? 'Already in the list below — pressing enter won\'t add a second copy.'
                    : 'Matches if the subject contains ANY of these phrases — press enter after each'}
                  FormHelperTextProps={subjectInputIsDuplicate ? { sx: { color: 'warning.main', fontWeight: 600 } } : undefined}
                />
              )}
            />

            <Autocomplete
              freeSolo size="small" fullWidth
              options={categoryOptions}
              value={form.category}
              onInputChange={(_, val) => set('category', val)}
              renderInput={(params) => (
                <TextField {...params} label="Category (optional)" helperText="Organizational label only — doesn't affect matching" />
              )}
            />

            <Stack direction="row" spacing={1} alignItems="flex-start">
              <ExpandableTextField
                label="Body pattern" size="small" fullWidth multiline
                minRows={4} maxRows={4} expandedMinRows={8} expandedMaxRows={16}
                value={form.bodyPattern} onChange={(e) => set('bodyPattern', e.target.value)}
                helperText={form.bodyPatternType === 'wildcard' ? 'Use % between segments, e.g. first phrase%second phrase' : 'Case-insensitive substring match'}
              />
              <FormControl size="small" sx={{ minWidth: 140, flexShrink: 0 }}>
                <InputLabel>Match type</InputLabel>
                <Select value={form.bodyPatternType} label="Match type" onChange={(e) => set('bodyPatternType', e.target.value)}>
                  <MenuItem value="substring">Contains</MenuItem>
                  <MenuItem value="wildcard">Wildcard (%)</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <FormControl size="small" fullWidth>
              <InputLabel>Intent</InputLabel>
              <Select value={form.intent} label="Intent" onChange={(e) => set('intent', e.target.value)}>
                <MenuItem value="">Any intent (checked before classification)</MenuItem>
                {intentOptionsFor([form.intent]).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </Select>
            </FormControl>

            <TextField
              label="Description (optional)" size="small" fullWidth
              value={form.description} onChange={(e) => set('description', e.target.value)}
            />

            {error && <Typography color="error" variant="body2">{error}</Typography>}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={busy}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" disabled={busy}
              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}>
              Save
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}
